/**
 * NIRVA ANIMATION ENGINE — Phase 1 + 3
 * Lenis smooth scroll + GSAP ScrollTrigger (properly proxied)
 * + Magnetic cursor + 3D tilt cards + Holographic shine + Collection tile depth
 */

(function () {
  'use strict';

  function init() {
    if (typeof gsap === 'undefined' || typeof Lenis === 'undefined') {
      setTimeout(init, 50);
      return;
    }
    initLenis();          // must be first
    initScrollTrigger();  // proxies ST to Lenis — must be second
    initScrollReveals();
    initMagneticButtons();
    initCursor();
    initCardTilt();
    initCollectionTiles();
    initMediaReveal();
  }

  /* ============================================================
     LENIS SMOOTH SCROLL
  ============================================================ */
  function initLenis() {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothTouch: false,
      syncTouch: false,
    });

    // Drive Lenis via GSAP ticker for perfectly frame-synced scroll
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    window.__nirvaLenis = lenis;
  }

  /* ============================================================
     SCROLLTRIGGER — properly wired to Lenis virtual scroll
  ============================================================ */
  function initScrollTrigger() {
    gsap.registerPlugin(ScrollTrigger);

    const lenis = window.__nirvaLenis;
    if (!lenis) return;

    // ScrollerProxy tells ScrollTrigger to read scroll position
    // from Lenis rather than native window.scrollY
    ScrollTrigger.scrollerProxy(document.body, {
      scrollTop(value) {
        if (arguments.length) {
          lenis.scrollTo(value, { immediate: true });
        }
        return lenis.scroll;
      },
      getBoundingClientRect() {
        return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
      },
      pinType: document.body.style.transform ? 'transform' : 'fixed',
    });

    // Keep ScrollTrigger in sync on every Lenis scroll event
    lenis.on('scroll', ScrollTrigger.update);

    // Also refresh after resize
    ScrollTrigger.addEventListener('refresh', () => lenis.resize());
    ScrollTrigger.refresh();
  }

  /* ============================================================
     SCROLL REVEAL ANIMATIONS
  ============================================================ */
  function initScrollReveals() {
    const fadeUpEls = document.querySelectorAll(
      '.nirva-reveal, h2, h3, .section__heading, .card, .collection-card, .product-card__info'
    );
    fadeUpEls.forEach((el, i) => {
      if (el.closest('.nirva-3d-hero')) return;
      gsap.fromTo(el,
        { opacity: 0, y: 48 },
        {
          opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
          delay: (i % 4) * 0.08,
          scrollTrigger: {
            trigger: el,
            scroller: document.body,
            start: 'top 88%',
            toggleActions: 'play none none none',
          },
        }
      );
    });

    const cardGrids = document.querySelectorAll(
      '.collection-list, .product-list__grid, .featured-collection__grid'
    );
    cardGrids.forEach((grid) => {
      const cards = grid.querySelectorAll(':scope > *');
      gsap.fromTo(cards,
        { opacity: 0, y: 60, scale: 0.96 },
        {
          opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'power3.out', stagger: 0.1,
          scrollTrigger: {
            trigger: grid,
            scroller: document.body,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
        }
      );
    });

    const marquees = document.querySelectorAll('.marquee__track, .marquee-inner');
    marquees.forEach((el) => {
      ScrollTrigger.create({
        trigger: el,
        scroller: document.body,
        start: 'top bottom',
        end: 'bottom top',
        onUpdate: (self) => {
          const velocity = self.getVelocity();
          gsap.to(el, { skewX: velocity / 800, overwrite: 'auto', duration: 0.4, ease: 'power2.out' });
        },
      });
    });

    const dividers = document.querySelectorAll('.divider hr, .section-divider');
    dividers.forEach((el) => {
      gsap.fromTo(el,
        { scaleX: 0, transformOrigin: 'left center' },
        {
          scaleX: 1, duration: 1.2, ease: 'power4.inOut',
          scrollTrigger: { trigger: el, scroller: document.body, start: 'top 90%' },
        }
      );
    });
  }

  /* ============================================================
     MAGNETIC BUTTONS
  ============================================================ */
  function initMagneticButtons() {
    const els = document.querySelectorAll('.button--primary, .button--secondary, .btn, [data-magnetic]');
    els.forEach((el) => {
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const dx = (e.clientX - (r.left + r.width / 2)) * 0.35;
        const dy = (e.clientY - (r.top + r.height / 2)) * 0.35;
        gsap.to(el, { x: dx, y: dy, duration: 0.3, ease: 'power2.out' });
      });
      el.addEventListener('mouseleave', () => {
        gsap.to(el, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.4)' });
      });
    });
  }

  /* ============================================================
     CUSTOM CURSOR
  ============================================================ */
  function initCursor() {
    if ('ontouchstart' in window) return;
    const cursor = document.createElement('div');
    cursor.id = 'nirva-cursor';
    cursor.innerHTML = '<div class="nirva-cursor__dot"></div><div class="nirva-cursor__ring"></div>';
    document.body.appendChild(cursor);
    const dot  = cursor.querySelector('.nirva-cursor__dot');
    const ring = cursor.querySelector('.nirva-cursor__ring');
    window.addEventListener('mousemove', (e) => {
      gsap.to(dot,  { x: e.clientX, y: e.clientY, duration: 0.1, ease: 'none' });
      gsap.to(ring, { x: e.clientX, y: e.clientY, duration: 0.5, ease: 'power2.out' });
    });
    document.querySelectorAll('a, button, .card, .collection-card, [data-cursor-expand]').forEach((el) => {
      el.addEventListener('mouseenter', () => cursor.classList.add('nirva-cursor--expand'));
      el.addEventListener('mouseleave', () => cursor.classList.remove('nirva-cursor--expand'));
    });
    document.body.style.cursor = 'none';
  }

  /* ============================================================
     PHASE 3A: 3D TILT + HOLOGRAPHIC SHINE ON PRODUCT CARDS
  ============================================================ */
  function initCardTilt() {
    if ('ontouchstart' in window) return;
    const cards = document.querySelectorAll(
      '.product-card, .card--product, .card-wrapper, [data-testid="product-card"], .resource-list__item'
    );
    cards.forEach((card) => {
      if (!card.querySelector('.nirva-shine')) {
        const shine = document.createElement('div');
        shine.className = 'nirva-shine';
        card.appendChild(shine);
      }
      const shine = card.querySelector('.nirva-shine');
      card.style.transformStyle = 'preserve-3d';
      card.style.willChange = 'transform';

      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const rotX = (-(e.clientY - (rect.top  + rect.height / 2)) / (rect.height / 2)) * 12;
        const rotY =  ((e.clientX - (rect.left + rect.width  / 2)) / (rect.width  / 2)) * 12;
        gsap.to(card, { rotateX: rotX, rotateY: rotY, scale: 1.03,
          boxShadow: `${-rotY * 1.5}px ${rotX * 1.5}px 40px rgba(0,0,0,0.25)`,
          duration: 0.3, ease: 'power2.out', transformPerspective: 900 });
        const sx = ((e.clientX - rect.left) / rect.width)  * 100;
        const sy = ((e.clientY - rect.top)  / rect.height) * 100;
        gsap.to(shine, { opacity: 0.18,
          background: `radial-gradient(circle at ${sx}% ${sy}%, rgba(255,255,255,0.55) 0%, rgba(120,200,255,0.15) 40%, transparent 70%)`,
          duration: 0.2, ease: 'none' });
      });
      card.addEventListener('mouseleave', () => {
        gsap.to(card,  { rotateX: 0, rotateY: 0, scale: 1,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)', duration: 0.6, ease: 'elastic.out(1, 0.5)' });
        gsap.to(shine, { opacity: 0, duration: 0.4, ease: 'power2.out' });
      });
    });
  }

  /* ============================================================
     PHASE 3B: COLLECTION TILE MAGNETIC HOVER + DEPTH SHADOW
  ============================================================ */
  function initCollectionTiles() {
    if ('ontouchstart' in window) return;
    const tiles = document.querySelectorAll(
      '.collection-card, .collection-list .resource-list__item, [data-testid="collection-card"]'
    );
    tiles.forEach((tile) => {
      const img = tile.querySelector('img, .collection-card__image');
      tile.addEventListener('mouseenter', () => {
        gsap.to(tile, { y: -6, boxShadow: '0 20px 60px rgba(0,0,0,0.22)', duration: 0.4, ease: 'power3.out' });
        if (img) gsap.to(img, { scale: 1.06, duration: 0.6, ease: 'power2.out' });
      });
      tile.addEventListener('mousemove', (e) => {
        const rect = tile.getBoundingClientRect();
        const dx = (e.clientX - (rect.left + rect.width  / 2)) * 0.04;
        const dy = (e.clientY - (rect.top  + rect.height / 2)) * 0.04;
        gsap.to(tile, { x: dx, y: dy - 6, duration: 0.3, ease: 'power2.out' });
      });
      tile.addEventListener('mouseleave', () => {
        gsap.to(tile, { x: 0, y: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', duration: 0.6, ease: 'elastic.out(1, 0.4)' });
        if (img) gsap.to(img, { scale: 1, duration: 0.5, ease: 'power2.inOut' });
      });
    });
  }

  /* ============================================================
     PHASE 3C: CINEMATIC MEDIA SECTION REVEAL
  ============================================================ */
  function initMediaReveal() {
    const mediaSections = document.querySelectorAll(
      '.media-with-content, .featured-product, .product-hotspots'
    );
    mediaSections.forEach((section) => {
      const media   = section.querySelector('img, video, .media');
      const content = section.querySelector('.media-with-content__content, .featured-product__info, .rte');
      if (media) {
        gsap.fromTo(media,
          { clipPath: 'inset(100% 0% 0% 0%)', scale: 1.08 },
          { clipPath: 'inset(0% 0% 0% 0%)', scale: 1, duration: 1.1, ease: 'power4.out',
            scrollTrigger: { trigger: section, scroller: document.body, start: 'top 75%', toggleActions: 'play none none none' } }
        );
      }
      if (content) {
        gsap.fromTo(content,
          { opacity: 0, x: 40 },
          { opacity: 1, x: 0, duration: 0.9, ease: 'power3.out', delay: 0.25,
            scrollTrigger: { trigger: section, scroller: document.body, start: 'top 75%', toggleActions: 'play none none none' } }
        );
      }
    });
  }

  /* ============================================================
     BOOT
  ============================================================ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
